// HAPPYCAP_AUTH_MOBILE_COMPACT_V1
// HAPPYCAP_AUTH_TYPO_UNIFIED_V1
import { Eye, EyeOff } from "lucide-react";
import { useState, useEffect } from "react";
import { registerUser } from "../services/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import Footer from "../components/Footer";

const TEMP_EMAIL_DOMAINS = [
  "tempmail.com", "10minutemail.com", "mailinator.com",
  "guerrillamail.com", "yopmail.com", "trashmail.com"
];

// ✅ Validación: + seguido de 11 o 12 dígitos
const isTelefonoValido = (tel) => /^\+\d{11,12}$/.test(tel);

export default function SignupAdult() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [telefono, setTelefono] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [msg, setMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const navigate = useNavigate();

  const isEmailValid = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) return false;
    const domain = email.split("@")[1];
    return !TEMP_EMAIL_DOMAINS.includes(domain);
  };

  useEffect(() => {
    const allFilled =
      email &&
      isEmailValid(email) &&
      password &&
      confirmPassword &&
      password === confirmPassword &&
      isTelefonoValido(telefono) &&
      confirm;
    setIsValid(allFilled);
  }, [email, password, confirmPassword, telefono, confirm]);

  const handleSignup = async () => {
    setMsg("");

    if (!email || !password || !confirmPassword || !telefono || !confirm) {
      setMsg(t("signup_minor.required_fields"));
      return;
    }

    if (!isValid) {
      setMsg(t("signup_adult.invalid_fields"));
      return;
    }

    setIsSubmitting(true);

    try {
      await registerUser({
        email,
        password,
        telefono,
        tipo_registro: "adulto",
        acepta_mayoria_edad: true,
        acepta_politica_funcionamiento: true,
        acepta_responsabilidad_menor: false,
        language: i18n.language || "es",
      });

      setMsg(t("signup_adult.success"));
      setTimeout(() => navigate("/activate", { state: { email } }), 2000);
    } catch (error) {
      console.error("Signup error:", error);
      setMsg("❌ " + (error.response?.data?.error || t("signup_adult.error")));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="w-[calc(100%-2.5rem)] max-w-sm sm:max-w-lg mx-auto mt-8 sm:mt-10 mb-10 bg-white border border-white/80 shadow-[0_16px_55px_rgba(22,63,99,0.09)] rounded-[24px] p-5 sm:p-9" role="main">
        <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-center mb-8 text-[#163F63]">
          {t("signup_adult.title")}
        </h2>

        <div className="space-y-4">
          <input
            type="email"
            placeholder={t("signup_adult.email")}
            aria-label={t("signup_adult.email")}
            aria-required="true"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8]"
          />

          <div className="relative">
            <input
            type={showPassword ? "text" : "password"}
            placeholder={t("signup_adult.password")}
            aria-label={t("signup_adult.password")}
            aria-required="true"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8] pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              aria-pressed={showPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#247BA8] hover:text-[#245580] focus:outline-none focus:ring-2 focus:ring-[#247BA8] rounded-lg p-1"
            >
              {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
            </button>
          </div>

          <div className="relative">
            <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder={t("signup_adult.confirm_password")}
            aria-label={t("signup_adult.confirm_password")}
            aria-required="true"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8] pr-12"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((visible) => !visible)}
              aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              aria-pressed={showConfirmPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#247BA8] hover:text-[#245580] focus:outline-none focus:ring-2 focus:ring-[#247BA8] rounded-lg p-1"
            >
              {showConfirmPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
            </button>
          </div>

          {/* 📱 Campo de teléfono con tooltip */}
          <div className="relative">
            <input
              type="tel"
              placeholder={t("signup_adult.phone")}
              aria-label={t("signup_adult.phone")}
              aria-required="true"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(!isTelefonoValido(telefono))}
              className="w-full border border-slate-300 bg-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#247BA8] focus:ring-2 focus:ring-[#247BA8]"
            />
            {showTooltip && !isTelefonoValido(telefono) && (
              <span className="absolute left-0 top-full mt-1 bg-red-600 text-white text-xs rounded px-2 py-1">
                {t("signup_adult.tooltip_phone")} <br />
                <strong>{t("signup_adult.example_phone", { defaultValue: "Ej: +34911222333" })}</strong>
              </span>
            )}
          </div>

          <div className="flex items-start gap-2 text-sm text-gray-700">
            <input
              id="checkbox-confirm"
              type="checkbox"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
              className="mt-1"
              aria-required="true"
              aria-label={t("signup_adult.confirm_plain")}
            />
            <label htmlFor="checkbox-confirm">
              <span
                dangerouslySetInnerHTML={{ __html: t("signup_adult.confirm") }}
              />
            </label>
          </div>

          {/* Botón */}
          <button
            onClick={handleSignup}
            disabled={isSubmitting}
            aria-label={t("signup_adult.submit")}
            className={`w-full py-3 px-4 rounded-xl font-semibold transition text-white shadow-md ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-orange-500 hover:bg-orange-600"
            }`}
          >
            {isSubmitting
              ? t("signup_adult.submitting")
              : t("signup_adult.submit")}
          </button>
        </div>

        {msg && (
          <p
            role="alert"
            aria-live="assertive"
            className={`mt-4 text-center text-sm font-medium ${
              msg.includes("❌") ? "text-red-600" : "text-green-600"
            }`}
          >
            {msg}
          </p>
        )}
      </div>

      <Footer loginStyle />
    </>
  );
}
